import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Grid,
  Snackbar,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { PhoneAndroid, PhoneIphone, Save } from "@mui/icons-material";
import mobileAppVersionService from "../../services/mobileAppVersionService";

const emptyForm = {
  minVersion: "1.0.0",
  latestVersion: "1.0.0",
  forceUpdate: false,
  storeUrl: "",
  releaseNotes: "",
};

const MobileAppVersions = () => {
  const [tab, setTab] = useState("android");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [forms, setForms] = useState({ android: { ...emptyForm }, ios: { ...emptyForm } });
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const load = async () => {
    setLoading(true);
    try {
      const res = await mobileAppVersionService.list();
      const rows = res?.data?.data || [];
      const next = { android: { ...emptyForm }, ios: { ...emptyForm } };
      rows.forEach((row) => {
        if (row.platform === "android" || row.platform === "ios") {
          next[row.platform] = {
            minVersion: row.minVersion || "1.0.0",
            latestVersion: row.latestVersion || "1.0.0",
            forceUpdate: !!row.forceUpdate,
            storeUrl: row.storeUrl || "",
            releaseNotes: row.releaseNotes || "",
          };
        }
      });
      setForms(next);
    } catch (e) {
      setSnack({
        open: true,
        message: e?.response?.data?.message || "Failed to load mobile app versions",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const current = forms[tab] || emptyForm;

  const save = async () => {
    setSaving(true);
    try {
      await mobileAppVersionService.save({ platform: tab, ...current });
      setSnack({ open: true, message: "Saved", severity: "success" });
      await load();
    } catch (e) {
      setSnack({
        open: true,
        message: e?.response?.data?.message || "Failed to save",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Mobile App Version Management
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure minimum and latest app versions for Android and iOS. Mobile clients can call{" "}
        <code>/api/v1/mobile-app-versions/check?platform=android|ios</code>.
      </Typography>

      <Card>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, borderBottom: 1, borderColor: "divider" }}>
          <Tab icon={<PhoneAndroid />} iconPosition="start" label="Android" value="android" />
          <Tab icon={<PhoneIphone />} iconPosition="start" label="iOS" value="ios" />
        </Tabs>
        <CardContent>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Minimum supported version"
                  value={current.minVersion}
                  onChange={(e) =>
                    setForms((prev) => ({
                      ...prev,
                      [tab]: { ...prev[tab], minVersion: e.target.value },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Latest published version"
                  value={current.latestVersion}
                  onChange={(e) =>
                    setForms((prev) => ({
                      ...prev,
                      [tab]: { ...prev[tab], latestVersion: e.target.value },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Store URL"
                  value={current.storeUrl}
                  onChange={(e) =>
                    setForms((prev) => ({
                      ...prev,
                      [tab]: { ...prev[tab], storeUrl: e.target.value },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Release notes"
                  value={current.releaseNotes}
                  onChange={(e) =>
                    setForms((prev) => ({
                      ...prev,
                      [tab]: { ...prev[tab], releaseNotes: e.target.value },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!current.forceUpdate}
                      onChange={(e) =>
                        setForms((prev) => ({
                          ...prev,
                          [tab]: { ...prev[tab], forceUpdate: e.target.checked },
                        }))
                      }
                    />
                  }
                  label="Force update below minimum version"
                />
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" startIcon={<Save />} disabled={saving} onClick={() => void save()}>
                  {saving ? "Saving..." : "Save"}
                </Button>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default MobileAppVersions;
